import * as React from "react"
import { SyntheticEvent } from "react"
import ApiClient, { QueryWhereClause } from "../api/ApiClient"
import { createLazyResource, formatNumber, safeNull } from "../common/Util"
import Region from "../models/Region"
import { observer } from "mobx-react"
import { observable } from "mobx"
import * as _ from 'lodash'
import { Alert, Button } from "reactstrap"
import BaseView from "./BaseView"
import { LinkContainer } from "react-router-bootstrap"
import { Routes } from "../routes/AppRoutes"
import { route } from "../routes/routes"
import { Link } from "react-router-dom"
import classNames from 'classnames'
import * as H from "history"
import { ApiTableViewAdapter } from "../components/table-view/ApiTableViewAdapter"
import Area from "../models/Area"
import RequireRole from "../components/RequireRole"
import { UserRole } from "../models/AuthUser"
import RequirePermission, { Permission } from "../components/RequirePermission"
import { ManagedChooseableColumn } from "../components/table-view/ManagedTableViewColumnChooser";
import ManagedTableView from "../components/table-view/ManagedTableView";
import AreasCsvTransformer from "../models/renderers/AreasCsvTransformer";
import AsyncSelectFilter, { AsyncSelectFilterTargetType } from '../components/table-view/filters/AsyncSelectFilter'
import { loadRegionOptionsForAdmin } from '../api/AsyncHelpers'

type Props = {
  location: H.Location<H.LocationState>,
}

@observer
export default class AreasView extends BaseView<Props> {
  @observable private numberOfActiveAreas = createLazyResource<number>(() => {
    return ApiClient.query(`areas { * }`, { returnTotal: true, limit: 0, where: [{ _scope: 'active' }] })
  }, response => response.data._meta.total);

  @observable private filteredRegion?: {id: number, name: string}

  componentWillMount (): void {
    this.filteredRegion = _.get(this, 'props.location.state.region')
  }

  renderContentHeader (): React.ReactNode | null {
    return (
      <>
        <h1>Areas</h1>
        <ul className="content-header-actions">
          <RequirePermission permission={Permission.CreateArea}>{() =>
            <li>
              <LinkContainer to={route(Routes.system.areas.create)}>
                <Button color="primary" onClick={() => {
                }}><i className="fa fa-plus-circle"/> Add New Area</Button>
              </LinkContainer>
            </li>
          }</RequirePermission>
        </ul>
      </>
    )
  }

  @observable private availableColumns: ManagedChooseableColumn[] = [
    {
      id: 'name',
      column: {
        title: 'Name',
        accessor: 'name',
        sortable: true,
        renderItem: (value, item) => (
          <Link type="link" to={{ pathname: route(Routes.system.areas.edit, { id: item.id }), state: { id: item.id } }}>
            <div className={classNames({ 'text-muted': item.status === 'Inactive' || item.region.status === 'Inactive' })}>
              {item.name}{item.status === 'Inactive' && ' (Inactive)'}
            </div>
          </Link>
        )
      },
      default: true
    },
    {
      id: 'region',
      column: {
        title: 'Region',
        accessor: 'region.name',
        sortable: true,
        renderItem: (value, item) => {
          return (<div className={classNames({ 'text-muted': item.region.status === 'Inactive' })}>
            {item.region.name}{item.region.status === 'Inactive' && ' (Inactive)'}
          </div>)
        }
      },
      default: true
    },
    {
      id: 'numberOfChapters',
      column: {
        title: 'Chapters',
        headerClassName: 'justify-content-center',
        accessor: 'numberOfChapters',
        renderItem: (value, item) => {
          return (
            <div className="text-center">
              <Link type="link" to={{ pathname: route(Routes.system.chapters.index), state: { area: { id: item.id, name: item.name } } }}>{item.numberOfChapters !== undefined ? formatNumber(item.numberOfChapters) : 'N/A'}</Link>
            </div>
          )
        },
      },
      default: true
    },
    {
      id: 'numberOfMembers',
      column: {
        title: 'Members',
        headerClassName: 'justify-content-center',
        accessor: 'numberOfMembers',
        renderItem: (value, item) => {
          return (
            <div className="text-center">
              <Link type="link" to={{ pathname: route(Routes.system.members.index), state: { area: { id: item.id, name: item.name } } }}>{item.numberOfMembers !== undefined ? formatNumber(item.numberOfMembers) : 'N/A'}</Link>
            </div>
          )
        },
      },
      default: true
    },
    {
      id: 'actions',
      column: {
        title: 'Actions',
        headerClassName: 'justify-content-center',
        renderItem: (value, item) => {
          return (
            <div className="table-actions">
              <Link type="link" to={{ pathname: route(Routes.system.areas.edit, { id: item.id }), state: { id: item.id } }}><i className="fa fa-edit"/></Link>
            </div>
          )
        }
      },
      fixed: true,
      hidden: true
    }
  ]

  private tableViewRef = React.createRef<ManagedTableView>()

  private areasAdapter = new ApiTableViewAdapter(() => {
      const where: QueryWhereClause[] = []

      if (this.filteredRegion) {
        where.push({
          _scope: 'region',
          value: this.filteredRegion.id,
        })
      }

      if (!safeNull(() => this.tableViewRef.current!.tableViewFilters!.showInactive)) {
        where.push({ _scope: 'active' })
      }

      return {
        query: `
      areas {
        *
        numberOfMembers
        numberOfChapters
        
        region {
          *
        }
      }
    `,
        where: where,
      }
    },
    'areas',
    Area
  )

  private removeRegionFilter = (ev: SyntheticEvent) => {
    ev.preventDefault()

    this.filteredRegion = undefined
    this.tableViewRef.current!.fetchData()
  }

  renderContentBody (): React.ReactNode {
    return this.renderTable();
  }

  private renderTableViewHeader = () => {
    return <RequireRole role={UserRole.Admin}>
      {() => <>
        <div className="list-view-info-icon">
          <i className="fa fa-map-marker"/>
        </div>
        <div className="list-view-info-text">
          <span>{this.numberOfActiveAreas.loading ? <i className="fa fa-spinner fa-spin"/> : formatNumber(this.numberOfActiveAreas.current!)}</span> Active Areas
        </div>
      </>}
    </RequireRole>
  }

  private renderFilters = () => {
    return <>
      {this.filteredRegion && (
        <Alert
          color="info"
        >
          You are currently only viewing areas in <b>{this.filteredRegion.name}</b><br/>
          <a onClick={this.removeRegionFilter.bind(this)} href="">Click here to view all areas</a>
        </Alert>
      )}
    </>
  }

  private filters = [
    new AsyncSelectFilter('region', AsyncSelectFilterTargetType.Field, 'regionId', 'Region', loadRegionOptionsForAdmin),
  ]

  private renderTable = () => {
    return <ManagedTableView
      stateKey="AreasView"
      hideItemCount={true}
      ref={this.tableViewRef}
      searchLabel="Find An Area"
      adapter={this.areasAdapter}
      defaultSort={{ sortKey: 'name' }}
      availableColumns={this.availableColumns}
      header={this.renderTableViewHeader()}
      tableHeader={this.renderFilters()}
      exportTransformer={AreasCsvTransformer}
      filters={this.filters}
    />
  }
}